import type { AxiosResponse } from "axios";
import { apiClient } from "../config/axiosConfig";
import { ERROR_500 } from "../constants/app-constant";
import type { EncounterDrgResponse } from "../types/encounter-drg";
import type { VBCAlertsV2Response } from "../types/vbc-alerts";
import {
  GET_CONVERSATION_LIST_ENDPOINT,
  GET_CONVERSATION_MESSAGE_LIST,
  PATIENT_CDI_QUERY_ENDPOINT,
  PATIENT_STATUS_LIST_ENDPOINT,
  PATIENT_TOPIC_LIST_ENDPOINT,
  POST_NEW_CONVERSATION,
  POST_NEW_MESSAGE,
  PDF_QUERY_ENDPOINT,
  QUERY_DETAIL_ANSWERS_ENDPOINT,
  UPDATE_QUERY_DETAIL_ENDPOINT,
  UPDATE_CONVERSATION_STATUS,
  UPDATE_MESSAGE_READ_STATUS,
  VISITED_PATIENT_DETAIL_ENDPOINT,
  // Add new endpoint constants
  GET_GRAPH_DATA_ENDPOINT,
  GET_FACILITIES_LIST_ENDPOINT,
  GET_SPECIALTIES_LIST_ENDPOINT,
  GET_DATE_START_LIST_ENDPOINT,
  GET_DATE_END_LIST_ENDPOINT,
  HTML_QUERY_ENDPOINT,
  GET_PROVIDER_DETAILS_ENDPOINT,
  GET_VBC_ALERT_LIST,
  UPDATE_VBC_ALERT_V2,
  GET_CDI_ALERT_LIST,
  GET_CDI_ALERT_LIST_V2,
  UPDATE_CDI_ALERT_V2,
  DOCUMENT_REFERENCE_CREATION_ENDPOINT,
  GET_ENCOUNTER_DRG_ENDPOINT,
  GET_CARE_MANAGEMENT_QUERY_ENDPOINT,
  GET_CLINICAL_CARE_GUIDELINES_ENDPOINT,
  GET_CLINICAL_CARE_GUIDELINES_LOOKUP_ENDPOINT,
  GET_CLINICAL_CARE_GUIDELINE_DETAIL_ENDPOINT,
  GET_CLINICAL_CARE_GUIDELINE_REVISION_ENDPOINT,
} from "../constants/arcplus-endpoint-constants";
import {
  UM_QUERIES_LIST_ENDPOINT,
  UM_QUERY_DETAIL_ENDPOINT,
} from "../constants/um-endpoint-constants";
import type {
  UmQueriesListResponse,
  UmQueryDetailResponse,
  UmQuerySubmitPayload,
} from "../types/um-queries";
import {
  OPCD_QUERIES_LIST_ENDPOINT,
  OPCD_HIM_ALERTS_ENDPOINT,
  OPCD_HIM_ALERT_ACKNOWLEDGE_ENDPOINT,
  OPCD_QUERY_DETAIL_ENDPOINT,
  OPCD_QUERY_RESPONSE_ENDPOINT,
  GET_OUTPATIENT_PRO_FEE_AUDIT_ALERTS_ENDPOINT,
  GET_OUTPATIENT_PRO_FEE_CODING_RESULTS_ENDPOINT,
} from "../constants/opcd-endpoint-constants";
import type {
  OpcdQueriesListResponse,
  OpcdQueryDetailResponse,
  OpcdQuerySubmitPayload,
} from "../types/opcd-queries";
import type { OpcdHimAlertsResponse } from "../types/opcd-him-alerts";
import type {
  ProFeeAuditAlertsParams,
  ProFeeAuditAlertsResponse,
} from "../types/pro-fee-audit";
import type {
  ProFeeCodingResultsParams,
  ProFeeCodingResultsResponse,
} from "../types/pro-fee-coding-results";
import type { CareManagementQueryApiResponse } from "../types/care-management";
import type { ClinicalCareGuidelinesListParams } from "../types/clinical-care-guidelines";

export const getQueryList = async (params: Record<string, any>) => {
  try {
    return await apiClient.get(PATIENT_CDI_QUERY_ENDPOINT, params);
  } catch (error: any) {
    console.error("Error fetching query list:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const getStatusList = async () => {
  try {
    return await apiClient.get(PATIENT_STATUS_LIST_ENDPOINT);
  } catch (error: any) {
    console.error("Error fetching status list:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const getTopicList = async () => {
  try {
    return await apiClient.get(PATIENT_TOPIC_LIST_ENDPOINT);
  } catch (error: any) {
    console.error("Error fetching status list:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const getQueryDetails = async (params: Record<string, any>) => {
  try {
    return await apiClient.get(VISITED_PATIENT_DETAIL_ENDPOINT, params);
  } catch (error: any) {
    console.error("Error fetching query list:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const getQueryDetailAnswers = async (params: Record<string, any>) => {
  try {
    return await apiClient.get(QUERY_DETAIL_ANSWERS_ENDPOINT, params);
  } catch (error: any) {
    console.error("Error fetching query list answers:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const updateQueryDetail = async (
  body: FormData,
  params: Record<string, unknown>,
) => {
  try {
    return await apiClient.put(UPDATE_QUERY_DETAIL_ENDPOINT, body, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params,
    });
  } catch (error: any) {
    console.error("Error updating query detail:", error);
    throw error;
  }
};

//HL7 MDM Document Reference
export const postMDMDocumentReference = async (
  body: FormData | Record<string, any>,
): Promise<any> => {
  try {
    // For FormData, don't set Content-Type header - let browser handle it
    // The axios config already handles this, but we explicitly pass config for clarity
    const config = body instanceof FormData ? {} : undefined;
    return await apiClient.post(
      DOCUMENT_REFERENCE_CREATION_ENDPOINT,
      body,
      config,
    );
  } catch (error) {
    console.error("Failed to post MDM Document Reference:", error);
    throw error;
  }
};

//Message Services

export const getConversationList = async (
  params: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.get(GET_CONVERSATION_LIST_ENDPOINT, params);
  } catch (error) {
    console.error("Failed to fetch conversation list:", error);
  }
};

export const getConversationMessageList = async (
  params: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.get(GET_CONVERSATION_MESSAGE_LIST, params);
  } catch (error) {
    console.error("Failed to fetch conversation message list:", error);
    throw error;
  }
};

export const postNewConversation = async (
  body: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.post(POST_NEW_CONVERSATION, body);
  } catch (error) {
    console.error("Failed to fetch conversation list:", error);
    return [];
  }
};

export const postNewMessage = async (
  body: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.post(POST_NEW_MESSAGE, body);
  } catch (error) {
    console.error("Failed to fetch conversation list:", error);
    return [];
  }
};

export const updateMessageReadStatus = async (
  body: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.put(UPDATE_MESSAGE_READ_STATUS, body);
  } catch (error) {
    console.error("Failed to fetch conversation list:", error);
    return [];
  }
};

export const updateConversationStatus = async (
  body: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.put(UPDATE_CONVERSATION_STATUS, body);
  } catch (error) {
    console.error("Failed to fetch conversation list:", error);
    return [];
  }
};

export const getPdfUrl = async (params: Record<string, any>) => {
  try {
    return await apiClient.get(PDF_QUERY_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Error fetching query PDF:", error);
    throw new Error(error?.message || ERROR_500);
  }
};
export const getHtmlUrl = async (params: Record<string, any>) => {
  try {
    return await apiClient.get(HTML_QUERY_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Error fetching query HTML:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

// New Dashboard and Filter Services

/**
 * Get graph data with filters
 */
export const getGraphData = async (params: Record<string, any>) => {
  try {
    return await apiClient.get(GET_GRAPH_DATA_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Error fetching graph data:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

/**
 * Get facilities list
 */
export const getFacilitiesList = async (params: Record<string, any>) => {
  try {
    return await apiClient.get(GET_FACILITIES_LIST_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Error fetching facilities list:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

/**
 * Get specialties list
 */
export const getSpecialtiesList = async (params: Record<string, any>) => {
  try {
    return await apiClient.get(GET_SPECIALTIES_LIST_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Error fetching specialties list:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

/**
 * Get provider dashboard start date filter
 */
export const getProviderDashboardStartFilter = async (
  params: Record<string, any>,
) => {
  try {
    return await apiClient.get(GET_DATE_START_LIST_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Error fetching provider dashboard start filter:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

/**
 * Get provider dashboard end date filter
 */
export const getProviderDashboardEndFilter = async (
  params: Record<string, any>,
) => {
  try {
    return await apiClient.get(GET_DATE_END_LIST_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Error fetching provider dashboard end filter:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const getProviderDetails = async (
  params: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.get(GET_PROVIDER_DETAILS_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Failed to fetch provider details:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const getVBCAlerts = async (
  params: Record<string, any>,
): Promise<AxiosResponse<VBCAlertsV2Response>> => {
  try {
    return await apiClient.get(GET_VBC_ALERT_LIST, params);
  } catch (error: any) {
    throw error;
  }
};

export const updateVBCAlertStatus = async (
  params: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.post(UPDATE_VBC_ALERT_V2, null, { params });
  } catch (error: any) {
    console.error("Failed to update VBC alert v2:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const getCDIAlerts = async (
  params: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.get(GET_CDI_ALERT_LIST, params);
  } catch (error: any) {
    throw new Error(error?.message || ERROR_500);
  }
};

export const getCDIAlertsV2 = async (
  params: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.get(GET_CDI_ALERT_LIST_V2, params);
  } catch (error: any) {
    throw new Error(error?.message || ERROR_500);
  }
};

export const updateCDIAlertStatusV2 = async (
  params: Record<string, any>,
): Promise<any> => {
  try {
    return await apiClient.post(UPDATE_CDI_ALERT_V2, null, { params });
  } catch (error) {
    console.error("Failed to update CDI alert v2:", error);
  }
};

export const getEncounterDrg = async (
  params: Record<string, any>,
): Promise<AxiosResponse<EncounterDrgResponse>> => {
  try {
    return await apiClient.get(GET_ENCOUNTER_DRG_ENDPOINT, { params });
  } catch (error: any) {
    throw error;
  }
};

export const getCareManagementQuery = async (params: {
  mrn: string;
  providerNpi: string;
}): Promise<CareManagementQueryApiResponse> => {
  try {
    return await apiClient.get(GET_CARE_MANAGEMENT_QUERY_ENDPOINT, { params });
  } catch (error: unknown) {
    console.error("Error fetching care management query:", error);
    throw error;
  }
};

export const getClinicalCareGuidelines = async (
  params: ClinicalCareGuidelinesListParams
) => {
  try {
    return await apiClient.get(GET_CLINICAL_CARE_GUIDELINES_ENDPOINT, {
      params,
    });
  } catch (error: unknown) {
    console.error("Error fetching clinical care guidelines:", error);
    throw error;
  }
};

export const lookupClinicalCareGuidelines = async (params: {
  conditions: string | string[];
  includeContent?: boolean;
}) => {
  try {
    return await apiClient.get(GET_CLINICAL_CARE_GUIDELINES_LOOKUP_ENDPOINT, {
      params,
      paramsSerializer: { indexes: null },
    });
  } catch (error: unknown) {
    console.error("Error looking up clinical care guidelines:", error);
    throw error;
  }
};

export const getClinicalCareGuidelineDetail = async (id: string) => {
  try {
    return await apiClient.get(GET_CLINICAL_CARE_GUIDELINE_DETAIL_ENDPOINT(id));
  } catch (error: unknown) {
    console.error("Error fetching clinical care guideline detail:", error);
    throw error;
  }
};

export const getClinicalCareGuidelineRevision = async (
  id: string,
  revisionNumber: number | string
) => {
  try {
    return await apiClient.get(
      GET_CLINICAL_CARE_GUIDELINE_REVISION_ENDPOINT(id, revisionNumber)
    );
  } catch (error: unknown) {
    console.error("Error fetching clinical care guideline revision:", error);
    throw error;
  }
};

export const getUmQueriesList = async (params: {
  provider_npi: string;
}): Promise<AxiosResponse<UmQueriesListResponse>> => {
  try {
    return await apiClient.get(UM_QUERIES_LIST_ENDPOINT, { params });
  } catch (error: any) {
    console.error("Error fetching UM queries list:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const submitUmQueryResponse = async (
  queryId: string,
  body: UmQuerySubmitPayload,
): Promise<AxiosResponse<UmQueryDetailResponse>> => {
  try {
    return await apiClient.post(UM_QUERY_DETAIL_ENDPOINT(queryId), body);
  } catch (error: any) {
    console.error("Error submitting UM query response:", error);
    throw error;
  }
};

export const getOpcdQueriesList = async (
  providerNpi: string
): Promise<AxiosResponse<OpcdQueriesListResponse>> => {
  try {
    return await apiClient.get(OPCD_QUERIES_LIST_ENDPOINT, {
      params: { providerNpi },
    });
  } catch (error: any) {
    console.error("Error fetching OPCD queries list:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const getOpcdQueryDetail = async (
  queryId: string,
  providerNpi: string
): Promise<AxiosResponse<OpcdQueryDetailResponse>> => {
  try {
    return await apiClient.get(OPCD_QUERY_DETAIL_ENDPOINT(queryId), {
      params: { providerNpi },
    });
  } catch (error: any) {
    console.error("Error fetching OPCD query:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const submitOpcdQueryResponse = async (
  queryId: string,
  body: OpcdQuerySubmitPayload
): Promise<AxiosResponse<OpcdQueryDetailResponse>> => {
  try {
    return await apiClient.post(OPCD_QUERY_RESPONSE_ENDPOINT(queryId), body);
  } catch (error) {
    console.error("Error submitting OPCD query response:", error);
    throw error;
  }
};

export const getOpcdHimAlerts = async (
  providerNpi: string
): Promise<AxiosResponse<OpcdHimAlertsResponse>> => {
  try {
    return await apiClient.get(OPCD_HIM_ALERTS_ENDPOINT, {
      params: { providerNpi },
    });
  } catch (error: any) {
    console.error("Error fetching OPCD HIM alerts:", error);
    throw new Error(error?.message || ERROR_500);
  }
};

export const acknowledgeOpcdHimAlert = async (
  alertId: string,
  providerNpi: string
): Promise<AxiosResponse<unknown>> => {
  try {
    return await apiClient.post(
      OPCD_HIM_ALERT_ACKNOWLEDGE_ENDPOINT(alertId),
      { providerNpi },
      { params: { providerNpi } }
    );
  } catch (error) {
    console.error("Error acknowledging OPCD HIM alert:", error);
    throw error;
  }
};

export const getOpcdCodingAlerts = async (
  params: ProFeeAuditAlertsParams
): Promise<AxiosResponse<ProFeeAuditAlertsResponse>> => {
  const query: Record<string, string> = {
    providerNpi: params.providerNpi,
  };
  if (params.mrn) query.mrn = params.mrn;
  if (params.caseId) query.caseId = params.caseId;
  if (params.dateOfService) query.dateOfService = params.dateOfService;

  try {
    return await apiClient.get(GET_OUTPATIENT_PRO_FEE_AUDIT_ALERTS_ENDPOINT, {
      params: query,
    });
  } catch (error: any) {
    console.error("Error fetching OPCD coding alerts:", error);
    throw error;
  }
};

export const getOpcdCodingResults = async (
  params: ProFeeCodingResultsParams
): Promise<AxiosResponse<ProFeeCodingResultsResponse>> => {
  const query: Record<string, string> = {
    providerNpi: params.providerNpi,
  };
  if (params.mrn) query.mrn = params.mrn;
  if (params.caseId) query.caseId = params.caseId;
  if (params.dateOfService) query.dateOfService = params.dateOfService;

  try {
    return await apiClient.get(GET_OUTPATIENT_PRO_FEE_CODING_RESULTS_ENDPOINT, {
      params: query,
    });
  } catch (error: any) {
    console.error("Error fetching OPCD coding results:", error);
    throw error;
  }
};
